import { ValidationRule, WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import { formRequiredRules } from '../../../../../lib/formRules';
import { REQUIRED_MSG } from '../../../../../constants/service/common/formRulesConstants';
import { PRODUCT_WIDGET_LAYOUT_TYPE_V2, ProductWidgetDetailV2 } from '../../../../../types/widget';
import { Validator } from '../../../../../types/common/form';
import { validateStringLength } from '../../../../../lib/utils/form/validators';

type ProductWidgetFormRuleReturnType = {
  isProductRecommend: { disabled: boolean };
  isEventConnect: { disabled: boolean };
  mainTitle: { rules: ValidationRule[] };
  subTitle: { rules: ValidationRule[] };
  memo: { rules: ValidationRule[] };
  buttonText: { hideIf?: boolean; rules: ValidationRule[] };
  event: { hideIf: boolean; rules: ValidationRule[] };
  detailImages: { hideIf: boolean; rules: ValidationRule[] };
  widgetItemProducts: { hideIf: boolean; rules: ValidationRule[] };
  topImage: { hideIf: boolean };
  isRandomItem: { hideIf: boolean };
  exposureWidgetItemProducts: {
    hideIf: boolean;
    rules: ValidationRule[];
  };
  exposureItemCount: { hideIf: boolean };
};

interface FormRulesProps {
  form: WrappedFormUtils;
  initialValue: Partial<ProductWidgetDetailV2>;
  isEdit: boolean;
}

export const useProductWidgetFormRules = (props: FormRulesProps): ProductWidgetFormRuleReturnType => {
  const { form, initialValue, isEdit } = props;
  const {
    isProductRecommend = initialValue.isProductRecommend,
    isRandomItem = initialValue.isRandomItem,
    buttonText = initialValue.buttonText,
    layoutType = initialValue.layoutType,
    isEventConnect = initialValue.isEventConnect,
  } = form.getFieldsValue(['isProductRecommend', 'isRandomItem', 'buttonText', 'layoutType', 'isEventConnect']) as Pick<
    ProductWidgetDetailV2,
    'isProductRecommend' | 'isRandomItem' | 'buttonText' | 'layoutType' | 'isEventConnect'
  >;

  const widgetItemProductsMinCountMap = {
    [PRODUCT_WIDGET_LAYOUT_TYPE_V2.GRID_2X2]: 4,
    [PRODUCT_WIDGET_LAYOUT_TYPE_V2.GRID_3X2]: 6,
    [PRODUCT_WIDGET_LAYOUT_TYPE_V2.GALLERY_3X3]: 9,
  };

  const widgetItemProductsMinCount =
    widgetItemProductsMinCountMap[
      layoutType as Exclude<PRODUCT_WIDGET_LAYOUT_TYPE_V2, 'swipeBig1xn' | 'swipeSmall1xn'>
    ];

  const exposureItemListValidator: Validator<ProductWidgetDetailV2['exposureWidgetItemProducts']> = (
    rule,
    value,
    callback,
  ) => {
    if (!isProductRecommend && !isRandomItem && (!value || value.length === 0)) {
      return callback(REQUIRED_MSG);
    }
    callback();
  };

  return {
    isProductRecommend: {
      disabled: isEdit || !!isEventConnect,
    },
    isEventConnect: {
      disabled: isEdit && !!isProductRecommend,
    },
    mainTitle: {
      rules: [
        formRequiredRules(true, REQUIRED_MSG),
        { validator: validateStringLength({ max: 20, message: '위젯 타이틀의 글자 수는 최대 20자입니다' }) },
      ],
    },
    subTitle: {
      rules: [
        { validator: validateStringLength({ max: 20, message: '위젯 서브 타이틀의 글자 수는 최대 20자입니다.' }) },
      ],
    },
    memo: {
      rules: [{ validator: validateStringLength({ max: 1000, message: '메모는 최대 글자 수는 1,000자입니다.' }) }],
    },
    buttonText: {
      hideIf: isProductRecommend,
      rules: [
        formRequiredRules(isEventConnect, REQUIRED_MSG),
        { validator: validateStringLength({ max: 10, message: '위젯 버튼명의 글자 수는 최대 10자입니다.' }) },
      ],
    },
    event: { hideIf: !isEventConnect, rules: [{ required: true, type: 'object', message: REQUIRED_MSG }] },
    //위젯 상세 페이지는 기획전이 연결되어 있거나, 위젯 버튼이 없으면 이동할 수 없기때문에 비노출
    detailImages: {
      hideIf: isEventConnect || !buttonText?.length,
      rules: [{ max: 100, type: 'array', message: '이미지는 최대 100개까지만 등록 가능합니다' }],
    },
    widgetItemProducts: {
      hideIf: !!isProductRecommend,
      rules: [
        { required: true, type: 'array', message: REQUIRED_MSG },
        {
          min: widgetItemProductsMinCount,
          type: 'array',
          message: `상품 아이템을 최소 ${widgetItemProductsMinCount}개 등록해주세요`,
        },
      ],
    },
    topImage: {
      hideIf:
        !(
          layoutType === PRODUCT_WIDGET_LAYOUT_TYPE_V2.SWIPE_BIG_1XN ||
          layoutType === PRODUCT_WIDGET_LAYOUT_TYPE_V2.SWIPE_SMALL_1XN
        ) || !buttonText?.length,
    },
    isRandomItem: { hideIf: !!isProductRecommend },
    exposureWidgetItemProducts: {
      hideIf: !!isProductRecommend || !!isRandomItem,
      rules: [{ validator: exposureItemListValidator }],
    },
    exposureItemCount: { hideIf: !isProductRecommend && !isRandomItem },
  };
};
